import { useEffect } from 'react';
import { isNoindexRoute } from './RouteMetaPolicy';

const setMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
    document.head.appendChild(element);
  }
  return element;
};

const setLink = (rel, href) => {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

const absoluteUrl = (value, fallback) => {
  try {
    return new URL(value || fallback, window.location.origin).toString();
  } catch {
    return fallback;
  }
};

const canonicalUrl = (value) => {
  const url = new URL(value || window.location.href, window.location.origin);
  url.hash = '';
  const allowed = new URLSearchParams();
  const tab = url.searchParams.get('tab');
  if (['private', 'commercial'].includes(tab)) allowed.set('tab', tab);
  url.search = allowed.toString();
  return url.toString();
};

const normalizeStructuredData = (structuredData) => {
  if (!Array.isArray(structuredData)) return structuredData;
  return {
    '@context': 'https://schema.org',
    '@graph': structuredData.map((entry) => {
      const { '@context': _context, ...rest } = entry;
      return rest;
    }),
  };
};

const Seo = ({
  title,
  description,
  image,
  imageAlt,
  url,
  type = 'website',
  structuredData,
  noindex = false,
}) => {
  useEffect(() => {
    const resolvedTitle = title ? `${title} | SchleiShare` : 'SchleiShare | Gemeinsam Lebensmittel retten';
    const resolvedDescription = String(description || 'Lebensmittel teilen und retten in der Schlei-Region.').slice(0, 170);
    const resolvedUrl = canonicalUrl(url);
    const resolvedImage = absoluteUrl(image, 'https://schleishare.de/og-image.png');
    const resolvedImageAlt = imageAlt || title || 'SchleiShare – Lebensmittel teilen in der Schlei-Region';
    const resolvedNoindex = noindex || isNoindexRoute(window.location.pathname);

    document.title = resolvedTitle;
    setLink('canonical', resolvedUrl);

    const tags = [
      ['meta[name="description"]', { name: 'description' }, resolvedDescription],
      ['meta[name="robots"]', { name: 'robots' }, resolvedNoindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'],
      ['meta[property="og:site_name"]', { property: 'og:site_name' }, 'SchleiShare'],
      ['meta[property="og:locale"]', { property: 'og:locale' }, 'de_DE'],
      ['meta[property="og:title"]', { property: 'og:title' }, resolvedTitle],
      ['meta[property="og:description"]', { property: 'og:description' }, resolvedDescription],
      ['meta[property="og:type"]', { property: 'og:type' }, type],
      ['meta[property="og:url"]', { property: 'og:url' }, resolvedUrl],
      ['meta[property="og:image"]', { property: 'og:image' }, resolvedImage],
      ['meta[property="og:image:width"]', { property: 'og:image:width' }, '1200'],
      ['meta[property="og:image:height"]', { property: 'og:image:height' }, '630'],
      ['meta[property="og:image:alt"]', { property: 'og:image:alt' }, resolvedImageAlt],
      ['meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image'],
      ['meta[name="twitter:url"]', { name: 'twitter:url' }, resolvedUrl],
      ['meta[name="twitter:title"]', { name: 'twitter:title' }, resolvedTitle],
      ['meta[name="twitter:description"]', { name: 'twitter:description' }, resolvedDescription],
      ['meta[name="twitter:image"]', { name: 'twitter:image' }, resolvedImage],
      ['meta[name="twitter:image:alt"]', { name: 'twitter:image:alt' }, resolvedImageAlt],
    ];

    tags.forEach(([selector, attributes, content]) => {
      setMeta(selector, attributes).setAttribute('content', content);
    });

    const scriptId = 'schleishare-structured-data';
    document.getElementById(scriptId)?.remove();
    if (structuredData) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(normalizeStructuredData(structuredData));
      document.head.appendChild(script);
    }

    return () => document.getElementById(scriptId)?.remove();
  }, [title, description, image, imageAlt, url, type, structuredData, noindex]);

  return null;
};

export default Seo;
